// src/services/job-location.service.ts
import { supabase } from "@/lib/supabase";
import { getCurrentOrganizationId } from "@/helpers/auth.helpers";
import type { JobLocation } from "@/types/geoTracking";

function mapJobLocation(row: Record<string, unknown>): JobLocation {
  return {
    id: row.id as string,
    organizationId: row.organization_id as string,
    jobId: row.job_id as string,
    latitude: Number(row.latitude),
    longitude: Number(row.longitude),
    address: (row.address as string) ?? null,
    geocodedAt: (row.geocoded_at as string) ?? null,
    geocodeSource: row.geocode_source as JobLocation["geocodeSource"],
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

export const JobLocationService = {
  /**
   * Fetch the stored coordinates for a job.
   * Returns null if not yet geocoded.
   */
  async getByJobId(jobId: string): Promise<JobLocation | null> {
    try {
      const { data, error } = await supabase
        .from("job_locations")
        .select("*")
        .eq("job_id", jobId)
        .maybeSingle();

      if (error) {
        console.error("[JobLocationService.getByJobId]", error);
        return null;
      }
      return data ? mapJobLocation(data as Record<string, unknown>) : null;
    } catch (err) {
      console.error("[JobLocationService.getByJobId] unexpected:", err);
      return null;
    }
  },

  /**
   * Trigger server-side geocoding for a job's address.
   * Calls the geocode-job Edge Function which writes to job_locations.
   * Called automatically after job creation.
   */
  async geocodeJob(jobId: string, address: string): Promise<void> {
    try {
      const { error } = await supabase.functions.invoke("geocode-job", {
        body: { job_id: jobId, address },
      });
      if (error) {
        console.error("[JobLocationService.geocodeJob]", error);
      }
    } catch (err) {
      console.error("[JobLocationService.geocodeJob] unexpected:", err);
    }
  },

  /**
   * Admin manual pin override — replaces any auto-geocoded coords.
   */
  async updateManualPin(
    jobId: string,
    latitude: number,
    longitude: number,
  ): Promise<JobLocation | null> {
    try {
      const orgId = await getCurrentOrganizationId();
      if (!orgId) return null;

      const { data, error } = await supabase
        .from("job_locations")
        .upsert(
          {
            organization_id: orgId,
            job_id: jobId,
            latitude,
            longitude,
            geocode_source: "manual",
            updated_at: new Date().toISOString(),
          },
          { onConflict: "job_id" },
        )
        .select()
        .single();

      if (error) {
        console.error("[JobLocationService.updateManualPin]", error);
        return null;
      }
      return mapJobLocation(data as Record<string, unknown>);
    } catch (err) {
      console.error("[JobLocationService.updateManualPin] unexpected:", err);
      return null;
    }
  },
};
